import { useAuth0 } from "@auth0/auth0-react";
import React from "react";
import { useQuery } from "@apollo/client";
import {CURRENT_USER_QUERY } from './api/user';
import {User as GraphQLUser, CompanyType, CampaignType, UserRole, Marketplace, V2Migration} from '../__generated__/graphql';

export interface TeaserFormat {
  type: string;
  displayName: string;
  minImageSize: {
    width: number;
    height: number;
  };
  recommendedAspectRatio: string;
  cloudinaryTransform: string;
}

export interface User extends Omit<GraphQLUser, 'company'> {
  company: {
    name: string,
    slug: string,
    type: CompanyType, _id: string,
    allowedMarketplaces: Marketplace[],
    allowMarketplaceCampaigns: boolean,
    allowFillerCampaigns: boolean,
    allowInHouseCampaigns: boolean,
    allowMarketplaceNativeCampaigns: boolean,
    allowInHouseNativeCampaigns: boolean,
    allowVevIntegration: boolean,
    allowMarketplaceCpa?: boolean,
    allowTeaserVideos: boolean,
    allowTeaserRibbon: boolean,
    allowPreferredDeals?: boolean,
    allowedTeaserFormats?: TeaserFormat[],
    targetingSections?: string[],
    v2migration?: V2Migration
  }
}
interface UserContext {
  user: User;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isAgencyOrAdvertiser: boolean;
}

export const CurrentUserContext = React.createContext<UserContext>({} as unknown as UserContext);

export const useCurrentUser = () => React.useContext(CurrentUserContext);
 
export function useFetchCurrentUser() {
    let { isLoading, isAuthenticated, error, loginWithRedirect, user: auth0user } = useAuth0();

    // Wait for Auth0 before fetching the user — otherwise the query is sent
    // without a token and the API responds with "Access denied"
    let { loading, error: queryError, data } = useQuery(CURRENT_USER_QUERY, {
      skip: isLoading || !isAuthenticated,
    });

  
    if (isLoading || error || !isAuthenticated) {
      if (error || queryError) {
        console.error("Auth0 user", auth0user, { error, queryError });
      }
      return { isLoading, isAuthenticated, error, loginWithRedirect };
    }
  
    if (loading || queryError || !data?.CurrentUser) {
      return {
        isLoading: loading,
        isAuthenticated: false,
        error: queryError,
        loginWithRedirect,
      };
    }

    const contimoUser = data.CurrentUser;
    if (!contimoUser.avatarUrl) {
        //TODO: add fallback avatar url
        contimoUser.avatarUrl = auth0user!.picture;
    }

    const isSuperAdmin = auth0user?.['https://api.contimo.app/userRole'] === UserRole.SuperAdmin

    const result = {
      isLoading: false,
      loading: false,
      isAuthenticated: true,
      user: contimoUser,
      auth0User: auth0user,
      loginWithRedirect,
      isAgencyOrAdvertiser: [CompanyType.Agency, CompanyType.Advertiser].includes(contimoUser.company.type),
      isSuperAdmin: auth0user?.['https://api.contimo.app/userRole'] === UserRole.SuperAdmin,
      isAdmin: isSuperAdmin || contimoUser.role === UserRole.AccountAdmin,
    };

    return result;
  }


  export const getAllowedCampaignTypes = (user: User | undefined) => {
    if (!user) {
      return {
        nativeContent: [],
        nativeDisplay: [],
      }
    }

    const {
      allowedMarketplaces,
      allowMarketplaceCampaigns,
      allowFillerCampaigns,
      allowInHouseCampaigns,
      allowMarketplaceNativeCampaigns,
      allowInHouseNativeCampaigns
    } = user.company
    
    const nativeContent = [
      ...(allowInHouseCampaigns ? [{ label: 'In-house', value: CampaignType.InHouse }] : []),
      ...(allowMarketplaceCampaigns && allowedMarketplaces.length > 0 ? [{ label: 'Marketplace', value: CampaignType.Standard }] : []),
      ...(allowFillerCampaigns? [{ label: 'Filler', value: CampaignType.Filler }] : []),
    ]

    const nativeDisplay = [
      ...(allowInHouseNativeCampaigns? [{ label: 'In-house (Native Display)', value: CampaignType.InHouseNativeDisplay }] : []),
      ...(allowMarketplaceNativeCampaigns? [{ label: 'Marketplace (Native Display)', value: CampaignType.MarketplaceNativeDisplay }] : []),
    ]

    return {
      nativeContent,
      nativeDisplay,
    }
  }

  // V1_ONLY companies must not see any links to V2. A missing value is treated as V1_ONLY.
  export const canSeeV2Links = (user: User | undefined) =>
    !!user?.company?.v2migration && user.company.v2migration !== V2Migration.V1Only
